import { HttpErrorResponse, HttpInterceptorFn } from "@angular/common/http";
import { inject } from "@angular/core";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { catchError, from, mergeMap, NEVER, throwError } from "rxjs";
import { environment } from "../../../environments/environment";
import { signInAgain, SIGNING_IN_AGAIN } from "../../factories/sign-in-again";
import { issuedByAnotherAuthority } from "../../factories/token-authority";

const authorityOrigin = new URL(environment.authority).origin;

export const authenticationInterceptor: HttpInterceptorFn = (req, next) => {
  // Skip relative/asset calls (./assets/...) and any request to the OIDC authority.
  // The IdP's discovery/token endpoints are anonymous; adding an Authorization header
  // forces a CORS preflight the authority rejects (Access-Control-Allow-Headers).
  if (!req.url.startsWith("http") || req.url.startsWith(authorityOrigin)) {
    return next(req);
  }

  const oidcSecurityService = inject(OidcSecurityService);
  const signingIn = inject(SIGNING_IN_AGAIN);
  return from(oidcSecurityService.getAccessToken()).pipe(
    mergeMap(token =>
      next(token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req).pipe(
        catchError((error: unknown) => {
          // A page opened before the login server changed (27 Oct 2026) still holds a token from the old one,
          // and every API refuses it. Forget it and sign in again; the new token is from this build's server,
          // so this cannot repeat. The request is not failed: the page is leaving for the sign-in, and an
          // error shown now is one the user can do nothing about.
          if (error instanceof HttpErrorResponse && error.status === 401 && issuedByAnotherAuthority(token, environment.authority)) {
            if (!signingIn.started) {
              signingIn.started = true;
              signInAgain(oidcSecurityService, true);
            }
            return NEVER;
          }
          return throwError(() => error);
        })
      )
    )
  );
}